import { Drawer } from 'theme';

const AUDIENCE_ID = 'd7209005da';

class InquiryDrawer extends Drawer {
    constructor() {
        super();
    }
    connectedCallback() {
        super.connectedCallback();

        // Prevent duplicate setup
        if (this.hasAttribute('data-initialized')) {
            return;
        }
        this.setAttribute('data-initialized', 'true');

        this.checkUrlAndOpen();

        this.setupFormValidation();

        this.restoreFormData();

        this.setupMailchimpForm();
    }

    checkUrlAndOpen() {
        const urlParams = new URLSearchParams(window.location.search);
        const hash = window.location.hash;

        if (urlParams.get('form_type') === 'contact' || hash.includes('inquiry-form')) {
            this.show();
        }
    }

    setupMailchimpForm() {
        const form = this.querySelector('#mc-embedded-subscribe-form');
        if (!form) return;

        // Prevent double submission
        let isSubmitting = false;

        form.addEventListener('submit', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (isSubmitting) return;

            // Run validation first
            const inputs = form.querySelectorAll('input[required], textarea[required], select[required]');
            let isValid = true;

            inputs.forEach((input) => {
                if (!this.validateField(input)) {
                    isValid = false;
                }
            });

            if (!isValid) {
                this.updateErrorDisplay();
                const errorResponse = this.querySelector('#mce-error-response');
                if (errorResponse) {
                    errorResponse.classList.remove('hidden');
                    errorResponse.textContent = 'Please fix the errors in the form.';
                    errorResponse.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                setTimeout(() => {
                    const firstInvalid = form.querySelector('[aria-invalid="true"]');
                    if (firstInvalid) firstInvalid.focus();
                }, 300);
                return;
            }

            isSubmitting = true;
            this.setLoadingState(true);

            // Collect form data manually since inputs may be outside form with form attribute
            const params = new URLSearchParams();
            
            // Get all inputs associated with this form (including those outside with form attribute)
            const formElements = this.querySelectorAll('input[form="mc-embedded-subscribe-form"], textarea[form="mc-embedded-subscribe-form"], select[form="mc-embedded-subscribe-form"]');
            const formElementsInside = form.querySelectorAll('input, textarea, select');
            
            // Combine both sets of elements and deduplicate by creating a Set based on element reference
            const allElementsSet = new Set([...formElementsInside, ...formElements]);
            const allElements = [...allElementsSet];
            
            // Collect form data from all elements
            const formDataObject = {};
            
            allElements.forEach(element => {
                if (element.name && element.value !== undefined) {
                    let fieldName = element.name;
                    let fieldValue = element.value;
                    
                    // Skip empty values
                    if (!fieldValue.trim()) {
                        return;
                    }
                    
                    // Handle different input types
                    if (element.type === 'checkbox' || element.type === 'radio') {
                        if (element.checked) {
                            params.append(fieldName, fieldValue);
                            formDataObject[fieldName] = fieldValue;
                        }
                    } else if (fieldValue.trim()) {
                        // Only append non-empty values
                        params.append(fieldName, fieldValue);
                        formDataObject[fieldName] = fieldValue;
                    }
                }
            });
            
            console.log('=== FORM DATA BEING SENT ===');
            console.log('Form data object:', formDataObject);
            console.log('URL params:', params.toString());
            console.log('=== END FORM DATA ===');

            // Use JSONP for proper response handling
            this.submitWithJSONP(form.action, params)
                .finally(() => {
                    isSubmitting = false;
                });
        });
    }

    submitWithJSONP(url, params) {
        return new Promise((resolve, reject) => {
        const successResponse = this.querySelector('#mce-success-response');
        const errorResponse = this.querySelector('#mce-error-response');
        const fieldset = this.querySelector('.fieldset');

        // Create callback function name
        const callbackName = 'mailchimpCallback' + Date.now();
        
        // Add JSONP parameters
        params.set('c', callbackName);
        
        // Modify URL to use JSONP endpoint
        const jsonpUrl = url.replace('/post?', '/post-json?') + '&' + params.toString();

        // Create script element for JSONP
        const script = document.createElement('script');
        script.src = jsonpUrl;

            // Create global callback
            window[callbackName] = (response) => {
                this.setLoadingState(false);

                // Clean up
                try {
                    document.head.removeChild(script);
                } catch (e) {
                    // Script might already be removed
                }
                delete window[callbackName];

                if (response.result === 'success') {
                    // Success - hide form and show success message
                    fieldset.style.display = 'none';
                    successResponse.classList.remove('hidden');
                    errorResponse.classList.add('hidden');
                    this.clearFormData();
                    successResponse.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    resolve(response);
                } else {
                    // Error from Mailchimp
                    let errorMessage = 'There was an error submitting the form. Please try again.';
                    
                    if (response.msg) {
                        // Clean up Mailchimp's error message (remove HTML and numbers)
                        errorMessage = response.msg.replace(/\d+ - /, '').replace(/<[^>]*>/g, '');
                    }

                    errorResponse.classList.remove('hidden');
                    errorResponse.textContent = errorMessage;
                    successResponse.classList.add('hidden');
                    errorResponse.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    reject(new Error(errorMessage));
                }
            };

            // Handle script loading errors
            script.onerror = () => {
                this.setLoadingState(false);
                try {
                    document.head.removeChild(script);
                } catch (e) {
                    // Script might already be removed
                }
                delete window[callbackName];
                
                const errorMessage = 'Network error. Please check your connection and try again.';
                errorResponse.classList.remove('hidden');
                errorResponse.textContent = errorMessage;
                successResponse.classList.add('hidden');
                errorResponse.scrollIntoView({ behavior: 'smooth', block: 'center' });
                reject(new Error(errorMessage));
            };

            // Add script to trigger the request
            document.head.appendChild(script);
        });
    }

    setLoadingState(loading) {
        const submitButton = this.querySelector('#mc-embedded-subscribe');
        const form = this.querySelector('#mc-embedded-subscribe-form');
        
        if (!submitButton || !form) {
            console.warn('Submit button or form not found in inquiry drawer');
            return;
        }
        
        if (loading) {
            submitButton.disabled = true;
            submitButton.classList.add('opacity-70', 'cursor-not-allowed', 'relative');
            
            // Store original content and add loading text + spinner
            if (!submitButton.dataset.originalContent) {
                submitButton.dataset.originalContent = submitButton.innerHTML;
            }
            
            // Create loading content with Tailwind classes
            submitButton.innerHTML = `
                <span class="flex items-center justify-center gap-2">
                    <div class="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin" 
                         style="animation: loading-spin 1s linear infinite;"></div>
                    Submitting...
                </span>
            `;
            
            // Disable all form inputs
            const inputs = form.querySelectorAll('input, textarea, select, button');
            inputs.forEach(input => {
                if (input !== submitButton) {
                    input.disabled = true;
                    input.classList.add('opacity-50');
                }
            });
        } else {
            submitButton.disabled = false;
            submitButton.classList.remove('opacity-70', 'cursor-not-allowed', 'relative');
            
            // Restore original content
            if (submitButton.dataset.originalContent) {
                submitButton.innerHTML = submitButton.dataset.originalContent;
            }
            
            // Re-enable all form inputs
            const inputs = form.querySelectorAll('input, textarea, select, button');
            inputs.forEach(input => {
                input.disabled = false;
                input.classList.remove('opacity-50');
            });
        }
    }

    setupFormValidation() {
        const form = this.querySelector('#mc-embedded-subscribe-form');
        if (!form) return;

        const inputs = form.querySelectorAll('input[required], textarea[required], select[required]');

        inputs.forEach((input) => {
            input.addEventListener('blur', () => {
                this.validateField(input);
                this.updateErrorDisplay();
            });

            input.addEventListener('input', () => {
                if (input.value.trim()) {
                    input.classList.remove('!border-red-500', '!bg-red-50');
                    input.style.borderColor = '';
                    input.removeAttribute('aria-invalid');
                }
                this.updateErrorDisplay();
                this.saveFormData();
            });
        });

        // Remove duplicate validation since it's now handled in setupMailchimpForm
        // This prevents validation conflicts
    }

    validateField(field) {
        const value = field.value.trim();
        let isValid = true;

        field.classList.remove('!border-red-500', '!bg-red-50');
        field.style.borderColor = '';
        field.removeAttribute('aria-invalid');

        if (!value) {
            isValid = false;
        } else if (field.type === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
            }
        } else if (field.type === 'tel') {
            const phoneRegex = /^[\d\s\-\+\(\)]+$/;
            if (!phoneRegex.test(value)) {
                isValid = false;
            }
        }

        if (!isValid) {
            field.classList.add('!border-red-500', '!bg-red-50');
            field.style.borderColor = '#ef4444';
            field.setAttribute('aria-invalid', 'true');
        }

        return isValid;
    }

    updateErrorDisplay() {
        const form = this.querySelector('#mc-embedded-subscribe-form');
        const errorResponse = this.querySelector('#mce-error-response');

        if (!errorResponse) return;

        const invalidFields = form.querySelectorAll('[aria-invalid="true"]');

        if (invalidFields.length > 0) {
            let errorMessages = [];

            invalidFields.forEach((field) => {
                const label =
                    field
                        .closest('.form-control')
                        ?.querySelector('label')
                        ?.textContent.replace('*', '')
                        .trim() ||
                    field.getAttribute('placeholder') ||
                    'This field';

                let errorMessage = '';

                if (!field.value.trim()) {
                    errorMessage = `${label} is required`;
                } else if (field.type === 'email') {
                    errorMessage = 'Please enter a valid email address';
                } else if (field.type === 'tel') {
                    errorMessage = 'Please enter a valid phone number';
                }

                if (errorMessage) {
                    errorMessages.push(errorMessage);
                }
            });

            if (errorMessages.length > 0) {
                errorResponse.classList.remove('hidden');
                errorResponse.innerHTML =
                    '<strong>Please fix the following errors:</strong><br>' + errorMessages.join('<br>');
            }
        } else {
            errorResponse.classList.add('hidden');
        }
    }

    getProductId() {
        const productInput = this.querySelector('input[name="contact[product_id]"]');
        return productInput ? productInput.value : window.location.pathname.split('/').pop();
    }

    getGlobalFields() {
        return ['FNAME', 'LNAME', 'EMAIL', 'LOCATION[country]', 'LOCATION[addr1]', 'SOCIAL'];
    }

    getProductFields() {
        return ['TYPE', 'DISPLAY', 'INTEREST', 'PAINTING'];
    }

    saveFormData() {
        const form = this.querySelector('#mc-embedded-subscribe-form');
        if (!form) return;

        const productId = this.getProductId();
        const globalFields = this.getGlobalFields();
        const productFields = this.getProductFields();

        const globalData = {};
        globalFields.forEach((fieldName) => {
            const field = form.querySelector(`[name="${fieldName}"]`);
            if (field && field.value.trim()) {
                globalData[fieldName] = field.value;
            }
        });

        if (Object.keys(globalData).length > 0) {
            localStorage.setItem('inquiry_form_global', JSON.stringify(globalData));
        }

        const productData = {
            timestamp: Date.now(),
            data: {}
        };

        productFields.forEach((fieldName) => {
            const field = form.querySelector(`[name="${fieldName}"]`);
            if (field && field.value.trim()) {
                productData.data[fieldName] = field.value;
            }
        });

        if (Object.keys(productData.data).length > 0) {
            localStorage.setItem(`inquiry_form_product_${productId}`, JSON.stringify(productData));
        }
    }

    restoreFormData() {
        const form = this.querySelector('#mc-embedded-subscribe-form');
        if (!form) return;

        try {
            const globalData = localStorage.getItem('inquiry_form_global');
            if (globalData) {
                const data = JSON.parse(globalData);
                Object.entries(data).forEach(([fieldName, value]) => {
                    const field = form.querySelector(`[name="${fieldName}"]`);
                    if (field && !field.value) {
                        field.value = value;
                    }
                });
            }
        } catch (e) {
            console.warn('Failed to restore global form data:', e);
        }

        try {
            const productId = this.getProductId();
            const productData = localStorage.getItem(`inquiry_form_product_${productId}`);

            if (productData) {
                const data = JSON.parse(productData);
                const now = Date.now();
                const fifteenMinutes = 15 * 60 * 1000;

                if (now - data.timestamp < fifteenMinutes) {
                    Object.entries(data.data).forEach(([fieldName, value]) => {
                        const field = form.querySelector(`[name="${fieldName}"]`);
                        if (field && !field.value) {
                            field.value = value;
                        }
                    });
                } else {
                    localStorage.removeItem(`inquiry_form_product_${productId}`);
                }
            }
        } catch (e) {
            console.warn('Failed to restore product form data:', e);
        }
    }

    clearFormData() {
        const productId = this.getProductId();
        localStorage.removeItem(`inquiry_form_product_${productId}`);
    }
}

if (!window.customElements.get('inquiry-drawer')) {
    window.customElements.define('inquiry-drawer', InquiryDrawer);
}
